import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { ProtectedRoute, RequireRole, getDefaultRoute } from '@/components/ProtectedRoute';
import Login from '@/pages/Login';
import DashboardLayout from '@/layouts/DashboardLayout';
import Overview from '@/pages/dashboard/Overview';
import WorkerHome from '@/pages/worker/WorkerHome';

// Lazy-loaded pages
const YieldTrends = lazy(() => import('@/pages/dashboard/YieldTrends'));
const Workers = lazy(() => import('@/pages/dashboard/Workers'));
const Packages = lazy(() => import('@/pages/dashboard/Packages'));
const PackageDetail = lazy(() => import('@/pages/dashboard/PackageDetail'));
const StoneLifecycle = lazy(() => import('@/pages/dashboard/StoneLifecycle'));
const Suppliers = lazy(() => import('@/pages/dashboard/Suppliers'));
const Alerts = lazy(() => import('@/pages/dashboard/Alerts'));
const CostSummary = lazy(() => import('@/pages/dashboard/CostSummary'));
const Stock = lazy(() => import('@/pages/dashboard/Stock'));
const Exports = lazy(() => import('@/pages/dashboard/Exports'));
const NewShipment = lazy(() => import('@/pages/operations/NewShipment'));
const AssignPolisher = lazy(() => import('@/pages/operations/AssignPolisher'));
const RecordReturn = lazy(() => import('@/pages/operations/RecordReturn'));
const LogRejection = lazy(() => import('@/pages/operations/LogRejection'));
const MarkExport = lazy(() => import('@/pages/operations/MarkExport'));
const SuppliersSettings = lazy(() => import('@/pages/settings/Suppliers'));
const StoneTypesSettings = lazy(() => import('@/pages/settings/StoneTypes'));
const WorkersSettings = lazy(() => import('@/pages/settings/Workers'));
const DataQuality = lazy(() => import('@/pages/settings/DataQuality'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Profile = lazy(() => import('@/pages/Profile'));
const VerifyReceipt = lazy(() => import('@/pages/dashboard/qc/VerifyReceipt'));
const HandoffConfirmation = lazy(() => import('@/pages/dashboard/qc/HandoffConfirmation'));
const SawingLog = lazy(() => import('@/pages/dashboard/SawingLog'));
const UserManagement = lazy(() => import('@/pages/admin/UserManagement'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      retry: 1,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-gold border-t-transparent" />
    </div>
  );
}

function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={getDefaultRoute(role)} replace />;
}

const ADMIN_ROLES = ['Owner', 'OperationsManager'];
const QC_ROLES = ['Owner', 'OperationsManager', 'QCInspector'];

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/worker"
              element={
                <ProtectedRoute>
                  <WorkerHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute>
                  <RequireRole roles={['SuperAdmin']}>
                    <UserManagement />
                  </RequireRole>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              {/* Analytics — Owner & OpsManager */}
              <Route index element={<RequireRole roles={ADMIN_ROLES}><Overview /></RequireRole>} />
              <Route path="yield" element={<RequireRole roles={ADMIN_ROLES}><YieldTrends /></RequireRole>} />
              <Route path="workers" element={<RequireRole roles={ADMIN_ROLES}><Workers /></RequireRole>} />
              <Route path="packages" element={<RequireRole roles={ADMIN_ROLES}><Packages /></RequireRole>} />
              <Route path="packages/:id" element={<RequireRole roles={ADMIN_ROLES}><PackageDetail /></RequireRole>} />
              <Route path="stone" element={<RequireRole roles={[...ADMIN_ROLES, 'QCInspector']}><StoneLifecycle /></RequireRole>} />
              <Route path="stone/:serial" element={<RequireRole roles={[...ADMIN_ROLES, 'QCInspector']}><StoneLifecycle /></RequireRole>} />
              <Route path="suppliers" element={<RequireRole roles={ADMIN_ROLES}><Suppliers /></RequireRole>} />
              <Route path="stock" element={<RequireRole roles={ADMIN_ROLES}><Stock /></RequireRole>} />
              <Route path="exports" element={<RequireRole roles={ADMIN_ROLES}><Exports /></RequireRole>} />
              <Route path="alerts" element={<RequireRole roles={ADMIN_ROLES}><Alerts /></RequireRole>} />
              <Route path="costs" element={<RequireRole roles={['Owner']}><CostSummary /></RequireRole>} />

              {/* Operations — Owner & OpsManager */}
              <Route path="new-shipment" element={<RequireRole roles={ADMIN_ROLES}><NewShipment /></RequireRole>} />
              <Route path="assign-polisher" element={<RequireRole roles={ADMIN_ROLES}><AssignPolisher /></RequireRole>} />
              <Route path="record-return" element={<RequireRole roles={QC_ROLES}><RecordReturn /></RequireRole>} />
              <Route path="log-rejection" element={<RequireRole roles={QC_ROLES}><LogRejection /></RequireRole>} />
              <Route path="mark-export" element={<RequireRole roles={ADMIN_ROLES}><MarkExport /></RequireRole>} />

              {/* QC Inspector screens */}
              <Route path="qc/verify-receipt" element={<RequireRole roles={QC_ROLES}><VerifyReceipt /></RequireRole>} />
              <Route path="qc/handoffs" element={<RequireRole roles={QC_ROLES}><HandoffConfirmation /></RequireRole>} />

              {/* Sawer screen */}
              <Route path="sawing" element={<RequireRole roles={['Owner', 'OperationsManager', 'Sawer']}><SawingLog /></RequireRole>} />

              {/* Settings — Owner & OpsManager */}
              <Route path="settings/suppliers" element={<RequireRole roles={ADMIN_ROLES}><SuppliersSettings /></RequireRole>} />
              <Route path="settings/stone-types" element={<RequireRole roles={ADMIN_ROLES}><StoneTypesSettings /></RequireRole>} />
              <Route path="settings/workers" element={<RequireRole roles={ADMIN_ROLES}><WorkersSettings /></RequireRole>} />
              <Route path="settings/data-quality" element={<RequireRole roles={ADMIN_ROLES}><DataQuality /></RequireRole>} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
